import React from 'react'
import { AlertCircle, Settings, X } from 'lucide-react'
import { startGoogleDriveAuth } from '../../services/googleDriveAuth'
import { useCloudStorage } from '../../hooks/useCloudStorage'
import toast from 'react-hot-toast'

/**
 * Banner component that appears when Google Drive connection expires
 * Provides streamlined reconnection with automatic token refresh fallback
 */
const CloudReconnectionBanner = ({ isVisible, onDismiss }) => {
  const { refreshAccessToken, resetReconnectionState, stopReconnectionAttempts } = useCloudStorage()
  
  if (!isVisible) return null

  /**
   * Streamlined reconnect flow:
   * 1. Check connectivity with Google first
   * 2. Try to refresh access token (silent, no user interaction)
   * 3. Only fall back to full OAuth if refresh fails
   */
  const handleReconnect = async () => {
    console.log('🔄 CloudReconnectionBanner: Starting streamlined reconnection flow...')
    
    // Reset any previous reconnection state
    resetReconnectionState()
    
    let loadingToast = toast.loading('🔄 Checking connectivity...', { id: 'reconnect-flow' })
    
    try {
      // Step 1: Check connectivity with Google services first
      console.log('🔄 CloudReconnectionBanner: Step 1 - Checking connectivity with Google...')
      
      // Check basic internet connectivity
      if (!navigator.onLine) {
        throw new Error('No internet connection detected. Please check your network and try again.')
      }
      
      // Check Google OAuth configuration before proceeding
      const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID
      const isClientIdConfigured = GOOGLE_CLIENT_ID && 
        GOOGLE_CLIENT_ID !== 'your-google-client-id' && 
        GOOGLE_CLIENT_ID.includes('.apps.googleusercontent.com')

      if (!isClientIdConfigured) {
        console.error('❌ CloudReconnectionBanner: Google Client ID not properly configured')
        throw new Error('Google Drive is not properly configured. Please contact support.')
      }

      // Skip connectivity test in production to avoid CORS issues
      console.log('🌐 CloudReconnectionBanner: Skipping connectivity test (production deployment)')
      toast.dismiss(loadingToast)
      loadingToast = toast.loading('🔄 Preparing reconnection...', { id: 'reconnect-flow' })
      
      // Step 2: Try to refresh access token using stored refresh token
      console.log('🔄 CloudReconnectionBanner: Step 2 - Attempting token refresh...')
      toast.dismiss(loadingToast)
      loadingToast = toast.loading('🔄 Attempting quick reconnection...', { id: 'reconnect-flow' })
      
      const refreshToken = localStorage.getItem('cloudRefreshToken')
      
      if (refreshToken) {
        console.log('🔑 CloudReconnectionBanner: Refresh token found, attempting silent refresh...')
        
        try {
          const newAccessToken = await refreshAccessToken()
          
          if (newAccessToken) {
            console.log('✅ CloudReconnectionBanner: Token refresh successful! No user action needed.')
            
            toast.dismiss(loadingToast)
            toast.success('🎉 Reconnected to Google Drive successfully!', {
              duration: 3000,
              id: 'reconnect-success'
            })
            
            // Hide the banner since we're reconnected
            onDismiss()
            return // Success! No need for full OAuth
          }
        } catch (refreshError) {
          console.log('❌ CloudReconnectionBanner: Token refresh failed:', refreshError.message)
          console.log('🔄 CloudReconnectionBanner: Falling back to full OAuth flow...')
        }
      } else {
        console.log('⚠️ CloudReconnectionBanner: No refresh token found, skipping to OAuth flow')
      }
      
      // Step 3: Fall back to full OAuth flow if refresh failed or no refresh token
      console.log('🔄 CloudReconnectionBanner: Step 3 - Starting full OAuth flow...')
      
      toast.dismiss(loadingToast)
      loadingToast = toast.loading('🌐 Redirecting to Google authentication...', { id: 'oauth-flow' })
      
      // Start Google Drive OAuth flow as fallback
      startGoogleDriveAuth()
      
      // The user will be redirected, so dismiss the loading toast after a delay
      setTimeout(() => {
        toast.dismiss(loadingToast)
      }, 2000)
      
    } catch (error) {
      console.error('❌ CloudReconnectionBanner: Reconnection flow failed:', error)
      
      toast.dismiss(loadingToast)
      toast.error(error.message || 'Failed to reconnect to Google Drive. Please try again.', {
        duration: 5000,
        id: 'reconnect-error'
      })
    }
  }

  return (
    <div className="bg-amber-50 border-b border-amber-200 px-4 py-3">
      <div className="flex items-center space-x-3">
        <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0" />
        <div className="flex-1">
          <p className="text-sm text-amber-800 font-medium">
            Google Drive connection expired
          </p>
          <p className="text-xs text-amber-700 mt-0.5">
            Click "Quick Reconnect" to test connectivity and restore sync automatically. Your notes are safe offline.
            {localStorage.getItem('cloudRefreshToken') ? 
              ' Quick reconnect will check Google connectivity, then try silent refresh before full re-authorization if needed.' : 
              ' Full re-authorization required after connectivity check.'}
          </p>
        </div>
      </div>
      
      <div className="flex items-center space-x-2">
        <button
          onClick={handleReconnect}
          className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-amber-800 bg-amber-100 hover:bg-amber-200 border border-amber-300 rounded-lg transition-colors duration-200"
        >
          <Settings className="w-3 h-3 mr-1" />
          Quick Reconnect
        </button>
        <button
          onClick={() => {
            stopReconnectionAttempts()
            toast.success('Stopped automatic reconnection attempts')
          }}
          className="inline-flex items-center px-2 py-1.5 text-xs font-medium text-amber-700 bg-amber-50 hover:bg-amber-100 border border-amber-200 rounded-lg transition-colors duration-200"
          title="Stop automatic reconnection attempts"
        >
          Stop Auto-Retry
        </button>
        <button
          onClick={onDismiss}
          className="p-1 text-amber-600 hover:text-amber-800 transition-colors duration-200"
          aria-label="Dismiss"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  )
}

export default CloudReconnectionBanner 